import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { CalculateInputTrigger } from "components/ui/CalculateInputTrigger/CalculateInputTrigger";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { MultiSelect } from "components/ui/MultiSelect/MultiSelect";
import { Select } from "components/ui/Select/Select";
import { AuditCCalculatorModal } from "features/alcoholRiskScore/AuditCCalculatorModal/AuditCCalculatorModal";
import { InterventionInfoHints } from "features/cdm/components/InterventionInfoHints/InterventionInfoHints";
import { getCdmField, isCdmFieldMandatory } from "features/cdm/utils/cdmMatrix";
import { readCdmMorbidityCarryForward } from "features/cdm/utils/cdmMorbidityCarryForward";
import {
  getWeightMeccBand,
  WEIGHT_MECC_BAND,
} from "features/cdm/utils/weightMecc";
import { CHA2DS2VASCCalculatorModal } from "features/cha2ds2vasc/CHA2DS2VASCCalculatorModal/CHA2DS2VASCCalculatorModal";
import { QRisk3CalculatorModal } from "features/qriskScore/QRisk3CalculatorModal/QRisk3CalculatorModal";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ItemFormWrapper } from "../../../../../components/ItemFormWrapper/ItemFormWrapper";
import {
  ALCOHOL_DEPENDENCE_INTERVENTIONS_OPTIONS,
  ALCOHOL_HIGHER_RISK_INTERVENTIONS_OPTIONS,
  ALCOHOL_INCREASING_RISK_INTERVENTIONS_OPTIONS,
  COPD_DYSPNOEA_OPTIONS,
  MECC_INTERVENTION_INFO,
  PHYSICAL_ACTIVITY_INTERVENTIONS_OPTIONS,
  PHYSICAL_ACTIVITY_Q1_OPTIONS,
  PHYSICAL_ACTIVITY_Q2_OPTIONS,
  SMOKING_INTERVENTIONS_OPTIONS,
  SMOKING_STATUS_OPTIONS,
  VAPING_STATUS_OPTIONS,
  WEIGHT_HIGH_RISK_INTERVENTIONS_OPTIONS,
  WEIGHT_INCREASED_RISK_INTERVENTIONS_OPTIONS,
  WEIGHT_NORMAL_INTERVENTIONS_OPTIONS,
  YES_NO_OPTIONS,
} from "../../../../../constants/formOptions";
import style from "./RiskAndAssesmentForm.module.css";

// Maps a registered CDM diagnosis to the QRisk3 condition it should pre-tick
// (KIN-048). Only diagnoses with a 1:1 QRisk3 equivalent are listed: Type 1
// diabetes has no CdmDisease enum and CKD isn't staged (QRisk3 needs 3–5), so
// those remain BE gaps and are intentionally absent here.
const CDM_REASON_TO_QRISK3_CONDITION = {
  DIABETES_TYPE_2: "b_type2",
  ATRIAL_FIBRILLATION: "b_AF",
};

// QRisk3 must not be calculated for patients with established atherosclerotic
// CVD. Per the HSE CDM Message Spec + Vendor Verification Plan, QRISK3 is "not
// required for IHD & CVD (Stroke/TIA)" and "not mandatory for Heart Failure or
// AFib unless also diagnosed with IHD or CVD Stroke/TIA". So only IHD, Stroke
// and TIA block it — Heart Failure and AFib do NOT (AFib is a QRISK3 input).
const CVD_DIAGNOSES_BLOCKING_QRISK3 = [
  "ISCHAEMIC_HEART_DISEASE",
  "STROKE",
  "TIA",
];

const QRISK3_GUIDANCE =
  "Only use QRISK3 for Treatment Programme patients if clinically relevant.";

// CHA2DS2-VASc stroke-risk interpretation per the CDM dataset source (clincalc):
// 0 = low, 1 = moderate, ≥2 = high.
const getCha2ds2vascRiskInterpretation = (score) => {
  if (score === "" || score === null || score === undefined) return null;

  const numericScore = Number(score);

  if (!Number.isFinite(numericScore)) return null;

  if (numericScore === 0) return "Low risk of stroke";
  if (numericScore === 1) return "Moderate risk of stroke";

  return "High risk of stroke";
};

const CHA2DS2VASC_LEGEND =
  "Legend: 0 = low · 1 = moderate · 2 or more = high.";
const QRISK3_CVD_BLOCKED_NOTE =
  "QRISK3 is not calculated for patients with established cardiovascular disease (ischaemic heart disease, stroke or TIA).";

// CDM dataset Ref 17: smoking intervention adds an "Other" option with a free-text
// box. CDM-only — BE `smokingInterventionOther` exists only on the CDM DTO, so the
// shared SMOKING_INTERVENTIONS_OPTIONS stays unchanged for PP/OCF.
const CDM_SMOKING_INTERVENTIONS_OPTIONS = [
  ...SMOKING_INTERVENTIONS_OPTIONS,
  { label: "Other (free text)", value: "OTHER" },
];

// CDM dataset Ref 19/20: the alcohol brief-intervention bands are LABELLED the
// reverse of the standard AUDIT / HSE Message Spec — Higher Risk = 8–15,
// Increasing Risk = 16–19, Possible Dependence = 20+. CDM follows its own dataset
// (product decision: dataset is the source of truth per programme); PP/OCF keep the
// shared spec-aligned mapping in utils/auditC. BE must mirror this CDM-only
// inversion — see `.claude/cdm-context/cdm-be-change-request-alcohol-bands.md`.
const getCdmAlcoholInterventionCategory = (fullAuditScore) => {
  const score = Number(fullAuditScore);

  if (!fullAuditScore || isNaN(score) || score < 8) return null;

  if (score <= 15) return "HIGHER_RISK";

  if (score <= 19) return "INCREASING_RISK";

  return "DEPENDENCE";
};

// Total Cholesterol : HDL ratio, derived from the bloods when both are present
// (spec Ref 37 — "Cholesterol/HDL ratio (calculated from above)").
const computeTcHdlRatio = (totalCholesterol, hdlCholesterol) => {
  const total = Number(totalCholesterol);
  const hdl = Number(hdlCholesterol);

  if (!total || !hdl || hdl <= 0) return "";

  return (total / hdl).toFixed(2);
};

const formSchema = (
  shouldQRisk3Score,
  bmi,
  shouldShowCopdDyspnoeaScore,
  shouldShowCha2ds2vascScore
) =>
  z
    .object({
      smokingStatus: z.string().min(1, "Smoking status is required"),
      vapingStatus: z.string().min(1, "Vaping status is required"),
      alcoholRiskScore: z.string().min(1, "Alcohol risk score is required"),
      alcoholFullAuditScore: z.string().optional(),
      ...(shouldQRisk3Score
        ? { qRisk3Score: z.string().min(1, "QRisk 3 score is required") }
        : {}),
      ...(shouldShowCopdDyspnoeaScore
        ? {
            copdDyspnoeaScore: z
              .string()
              .min(1, "COPD dyspnoea score is required"),
          }
        : {}),
      ...(shouldShowCha2ds2vascScore
        ? {
            cha2ds2vascScore: z
              .string()
              .min(1, "CHA2DS2-VASc score is required"),
          }
        : {}),
      physicalActivityQ1: z.string().min(1, "Physical activity is required"),
      physicalActivityQ2: z.string().min(1, "Physical activity is required"),
      smokingInterventions: z.array(z.string()).optional(),
      smokingInterventionOther: z.string().optional(),
      alcoholDependenceInterventions: z.array(z.string()).optional(),
      alcoholHigherRiskInterventions: z.array(z.string()).optional(),
      alcoholIncreasingRiskInterventions: z.array(z.string()).optional(),
      weightHighRiskInterventions: z.array(z.string()).optional(),
      weightIncreasedRiskInterventions: z.array(z.string()).optional(),
      weightNormalInterventions: z.array(z.string()).optional(),
      physicalActivityInterventions: z.array(z.string()).optional(),
      medicationReview: z.string().min(1, "Medication review is required"),
    })
    .superRefine((data, ctx) => {
      if (
        data.smokingStatus === "CURRENT" &&
        !data.smokingInterventions?.length
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["smokingInterventions"],
          message: "Smoking interventions is required",
        });
      }

      if (
        data.smokingInterventions?.includes("OTHER") &&
        !data.smokingInterventionOther
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["smokingInterventionOther"],
          message: "Please specify the smoking intervention",
        });
      }

      const category = getCdmAlcoholInterventionCategory(
        data.alcoholFullAuditScore
      );

      if (
        category === "DEPENDENCE" &&
        !data.alcoholDependenceInterventions?.length
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["alcoholDependenceInterventions"],
          message: "Alcohol dependence interventions is required",
        });
      }

      if (
        category === "HIGHER_RISK" &&
        !data.alcoholHigherRiskInterventions?.length
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["alcoholHigherRiskInterventions"],
          message: "Alcohol higher risk interventions is required",
        });
      }

      if (
        category === "INCREASING_RISK" &&
        !data.alcoholIncreasingRiskInterventions?.length
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["alcoholIncreasingRiskInterventions"],
          message: "Alcohol increasing risk interventions is required",
        });
      }

      const isLowActivity =
        (data.physicalActivityQ1 === "ZERO_DAYS" ||
          data.physicalActivityQ1 === "ONE_TO_FOUR_DAYS") &&
        data.physicalActivityQ2 === "NO_INADEQUATE";

      if (isLowActivity && !data.physicalActivityInterventions?.length) {
        ctx.addIssue({
          code: "custom",
          path: ["physicalActivityInterventions"],
          message: "Physical activity interventions is required",
        });
      }

      const weightMeccBand = getWeightMeccBand(bmi);

      if (
        weightMeccBand === WEIGHT_MECC_BAND.HIGH &&
        !data.weightHighRiskInterventions?.length
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["weightHighRiskInterventions"],
          message: "Weight high risk interventions is required",
        });
      }

      if (
        weightMeccBand === WEIGHT_MECC_BAND.INCREASED &&
        !data.weightIncreasedRiskInterventions?.length
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["weightIncreasedRiskInterventions"],
          message: "Weight increased risk interventions is required",
        });
      }

      if (
        weightMeccBand === WEIGHT_MECC_BAND.NORMAL &&
        !data.weightNormalInterventions?.length
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["weightNormalInterventions"],
          message: "Weight normal interventions is required",
        });
      }
    });

export const RiskAndAssessmentForm = ({
  onBack,
  onContinue,
  data,
  patientId,
  carriedFactorsFromApi,
  isReview = false,
  type,
  registrationReasons,
}) => {
  // Morbidities recorded on the patient's most recent completed CDM review,
  // used to pre-seed the CHA2DS2-VASc / QRisk3 calculators on a new review.
  // `carriedFactorsFromApi` (round-tripped from the prior review's DTO) is the
  // source of truth; this localStorage snapshot is only a fallback. A selection
  // already saved on THIS review always wins (see defaultValues).
  const carriedMorbidities = useMemo(
    () => readCdmMorbidityCarryForward(patientId),
    [patientId]
  );
  // mMRC Dyspnoea Score lives here (Disease Assessment Scoring, with QRisk3) —
  // moved from the Plan & Outcome step. Shown/required for COPD per the matrix.
  const shouldShowCopdDyspnoeaScore = getCdmField(
    registrationReasons,
    "copdDyspnoeaScore",
    type
  );
  // CHA2DS2-VASc lives here too (Disease Assessment Scoring) — moved from the
  // Plan & Outcome step (KIN-1185). Shown/required for AFib per the matrix.
  const shouldShowCha2ds2vascScore = getCdmField(
    registrationReasons,
    "cha2ds2vascScore",
    type
  );

  const isQRisk3Relevant = getCdmField(registrationReasons, "qRisk3", type);
  const isQRisk3MandatoryByMatrix = isCdmFieldMandatory(
    registrationReasons,
    "qRisk3",
    type
  );
  const hasEstablishedCvd = (registrationReasons ?? []).some((reason) =>
    CVD_DIAGNOSES_BLOCKING_QRISK3.includes(reason)
  );
  // Block QRisk3 for established CVD: don't show the calculator, and don't
  // require a score so the GP can still complete the review.
  // Shown when relevant (incl. Yes*), but only REQUIRED when the matrix marks it
  // mandatory and the patient has no established CVD (block).
  const isQRisk3Required = isQRisk3MandatoryByMatrix && !hasEstablishedCvd;

  // KIN-048: pre-tick QRisk3 conditions the patient is already registered for
  // under CDM. Only the diagnoses with a QRisk3 equivalent are mapped here —
  // Type 1 (no CdmDisease enum) and CKD staging (3–5) are BE gaps.
  const diagnosisQRisk3Conditions = (registrationReasons ?? []).reduce(
    (conditions, reason) => {
      const conditionKey = CDM_REASON_TO_QRISK3_CONDITION[reason];

      if (conditionKey) conditions[conditionKey] = true;

      return conditions;
    },
    {}
  );

  const [isQRisk3ModalOpen, setIsQRisk3ModalOpen] = useState(false);
  const [isAuditCModalOpen, setIsAuditCModalOpen] = useState(false);
  const [isCha2ds2vascModalOpen, setIsCha2ds2vascModalOpen] = useState(false);

  // Weight brief-intervention band is determined by BMI (Ref 25–27 / X0139). Only
  // the ONE band matching the BMI may be submitted (BE rejects multiple), so we
  // render and seed only that band.
  const weightMeccBand = getWeightMeccBand(data?.bmi);

  const form = useForm({
    resolver: zodResolver(
      formSchema(
        isQRisk3Required,
        data?.bmi,
        shouldShowCopdDyspnoeaScore,
        shouldShowCha2ds2vascScore
      )
    ),
    defaultValues: {
      attendingHospital: data?.attendingHospital || "",
      smokingStatus: data?.smokingStatus || "",
      vapingStatus: data?.vapingStatus || "",
      alcoholRiskScore: data?.alcoholRiskScore || "",
      alcoholFullAuditScore: data?.alcoholFullAuditScore || "",
      auditCQ1: data?.auditCQ1 ?? null,
      auditCQ2: data?.auditCQ2 ?? null,
      auditCQ3: data?.auditCQ3 ?? null,
      auditQ4: null,
      auditQ5: null,
      auditQ6: null,
      auditQ7: null,
      auditQ8: null,
      auditQ9: null,
      auditQ10: null,
      physicalActivity: data?.physicalActivity || "",
      qRisk3Score: (isQRisk3Relevant && data?.qRisk3Score) || "",
      copdDyspnoeaScore:
        (shouldShowCopdDyspnoeaScore && data?.copdDyspnoeaScore) || "",
      cha2ds2vascScore:
        (shouldShowCha2ds2vascScore &&
          (data?.cha2ds2vascScore ??
            carriedFactorsFromApi?.cha2ds2vascScore ??
            carriedMorbidities.cha2ds2vascScore)) ||
        "",
      cha2ds2vascFactors:
        data?.cha2ds2vascFactors ??
        carriedFactorsFromApi?.cha2ds2vascFactors ??
        carriedMorbidities.cha2ds2vascFactors ??
        {},
      tcHdlRatio:
        data?.tcHdlRatio ||
        computeTcHdlRatio(data?.totalCholesterol, data?.hdlCholesterol),
      qRisk3TotalCholesterol:
        data?.qRisk3TotalCholesterol || data?.totalCholesterol || "",
      qRisk3Hdl: data?.qRisk3Hdl || data?.hdlCholesterol || "",
      qRisk3Sbps5: data?.qRisk3Sbps5 || "",
      // null (not 1) so the calculator can seed ethnicity from the patient's HSE
      // ethnic group on a new review; a stored review value still wins.
      qRisk3Ethrisk: data?.qRisk3Ethrisk ?? null,
      // Seed from the registered diagnosis (KIN-048), then carry forward the
      // previous review's conditions (API wins over the localStorage fallback);
      // any saved selection on this review wins.
      qRisk3Conditions: {
        ...diagnosisQRisk3Conditions,
        ...(carriedMorbidities.qRisk3Conditions ?? {}),
        ...(carriedFactorsFromApi?.qRisk3Conditions ?? {}),
        ...(data?.qRisk3Conditions ?? {}),
      },
      qRisk3CigarettesPerDay: data?.qRisk3CigarettesPerDay || "",
      edAttendancesLast6Months: data?.edAttendancesLast6Months || "",
      unscheduledAdmissionsLast6Months:
        data?.unscheduledAdmissionsLast6Months || "",
      physicalActivityQ1: data?.physicalActivityQ1 || "",
      physicalActivityQ2: data?.physicalActivityQ2 || "",
      smokingInterventions: data?.smokingInterventions || [],
      smokingInterventionOther: data?.smokingInterventionOther || "",
      alcoholDependenceInterventions:
        data?.alcoholDependenceInterventions || [],
      alcoholHigherRiskInterventions:
        data?.alcoholHigherRiskInterventions || [],
      alcoholIncreasingRiskInterventions:
        data?.alcoholIncreasingRiskInterventions || [],
      // Seed only the BMI-matching band; the others stay empty so a single
      // weight risk category is ever submitted (BE rejects multiple — X0139).
      weightHighRiskInterventions:
        weightMeccBand === WEIGHT_MECC_BAND.HIGH
          ? data?.weightHighRiskInterventions || []
          : [],
      weightIncreasedRiskInterventions:
        weightMeccBand === WEIGHT_MECC_BAND.INCREASED
          ? data?.weightIncreasedRiskInterventions || []
          : [],
      weightNormalInterventions:
        weightMeccBand === WEIGHT_MECC_BAND.NORMAL
          ? data?.weightNormalInterventions || []
          : [],
      physicalActivityInterventions: data?.physicalActivityInterventions || [],
      medicationReview: data?.medicationReview || "",
    },
  });

  const alcoholFullAuditScore = form.watch("alcoholFullAuditScore");
  const alcoholInterventionCategory = getCdmAlcoholInterventionCategory(
    alcoholFullAuditScore
  );

  useEffect(() => {
    if (alcoholInterventionCategory !== "DEPENDENCE")
      form.setValue("alcoholDependenceInterventions", []);
    if (alcoholInterventionCategory !== "HIGHER_RISK")
      form.setValue("alcoholHigherRiskInterventions", []);
    if (alcoholInterventionCategory !== "INCREASING_RISK")
      form.setValue("alcoholIncreasingRiskInterventions", []);
  }, [alcoholInterventionCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const physicalActivityQ1 = form.watch("physicalActivityQ1");
  const physicalActivityQ2 = form.watch("physicalActivityQ2");
  const isLowActivity =
    (physicalActivityQ1 === "ZERO_DAYS" ||
      physicalActivityQ1 === "ONE_TO_FOUR_DAYS") &&
    physicalActivityQ2 === "NO_INADEQUATE";

  useEffect(() => {
    if (!isLowActivity) form.setValue("physicalActivityInterventions", []);
  }, [isLowActivity]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("RiskAndAssessmentForm"))}
      isReview={isReview}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("RiskAndAssessmentForm"))}
          >
            <div className={style.itemWrapper}>
              <h3>Disease Assessment Scoring</h3>
              <div
                className={style.inputGroupGrid4Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="medicationReview">
                  <label className={style.addReferralFormLabel}>
                    Medication review completed?
                  </label>
                  <Select
                    options={YES_NO_OPTIONS.map((o) => ({
                      ...o,
                      value: o.value.toLowerCase(),
                    }))}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("medicationReview")}
                    onChange={(value) =>
                      form.setValue("medicationReview", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                </FormField>
                {isQRisk3Relevant && (
                  <FormField name="qRisk3Score">
                    <label className={style.addReferralFormLabel}>
                      QRISK3 Score
                    </label>
                    <input type="hidden" {...form.register("qRisk3Score")} />
                    {hasEstablishedCvd ? (
                      <p className={style.qRiskNote}>
                        {QRISK3_CVD_BLOCKED_NOTE}
                      </p>
                    ) : (
                      <>
                        <CalculateInputTrigger
                          value={form.watch("qRisk3Score")}
                          onClick={() => setIsQRisk3ModalOpen(true)}
                          disabled={isReview}
                        />
                        <p className={style.qRiskHint}>{QRISK3_GUIDANCE}</p>
                      </>
                    )}
                  </FormField>
                )}
                {shouldShowCopdDyspnoeaScore && (
                  <FormField name="copdDyspnoeaScore">
                    <label className={style.addReferralFormLabel}>
                      mMRC Dyspnoea Score
                    </label>
                    <Select
                      options={COPD_DYSPNOEA_OPTIONS}
                      placeholder="Select"
                      value={form.watch("copdDyspnoeaScore")}
                      onChange={(value) =>
                        form.setValue("copdDyspnoeaScore", value, {
                          shouldValidate: true,
                        })
                      }
                      disabled={isReview}
                      required
                    />
                  </FormField>
                )}
                {shouldShowCha2ds2vascScore && (
                  <FormField name="cha2ds2vascScore">
                    <label className={style.addReferralFormLabel}>
                      CHA2DS2-VASc Score
                    </label>
                    <input
                      type="hidden"
                      {...form.register("cha2ds2vascScore")}
                    />
                    <CalculateInputTrigger
                      value={form.watch("cha2ds2vascScore")}
                      onClick={() => setIsCha2ds2vascModalOpen(true)}
                      disabled={isReview}
                    />
                    {!!getCha2ds2vascRiskInterpretation(
                      form.watch("cha2ds2vascScore")
                    ) && (
                      <p className={style.qRiskHint}>
                        Risk:{" "}
                        {getCha2ds2vascRiskInterpretation(
                          form.watch("cha2ds2vascScore")
                        )}
                      </p>
                    )}
                    <p className={style.qRiskHint}>{CHA2DS2VASC_LEGEND}</p>
                  </FormField>
                )}
              </div>
            </div>
            <div>
              <h3 className={style.headingWithInfo}>
                Risk Factors
                <InfoTooltip text={MECC_INTERVENTION_INFO} />
              </h3>

              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "20px" }}
              >
                <FormField name="smokingStatus">
                  <label className={style.addReferralFormLabel}>
                    Smoking Status
                  </label>
                  <Select
                    options={SMOKING_STATUS_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("smokingStatus")}
                    onChange={(value) =>
                      form.setValue("smokingStatus", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                </FormField>
                <FormField name="vapingStatus">
                  <label className={style.addReferralFormLabel}>
                    Vaping Status
                  </label>
                  <Select
                    options={VAPING_STATUS_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("vapingStatus")}
                    onChange={(value) =>
                      form.setValue("vapingStatus", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                </FormField>
                <FormField name="smokingInterventions">
                  <label className={style.addReferralFormLabel}>
                    Smoking Interventions
                    {form.watch("smokingStatus") !== "CURRENT" && " (optional)"}
                  </label>
                  <MultiSelect
                    options={CDM_SMOKING_INTERVENTIONS_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("smokingInterventions")}
                    onChange={(value) => {
                      form.setValue("smokingInterventions", value, {
                        shouldValidate: true,
                      });
                      if (!value.includes("OTHER")) {
                        form.setValue("smokingInterventionOther", "", {
                          shouldValidate: true,
                        });
                      }
                    }}
                  />
                  <InterventionInfoHints
                    options={CDM_SMOKING_INTERVENTIONS_OPTIONS}
                    selectedValues={form.watch("smokingInterventions")}
                    className={style.qRiskHint}
                  />
                </FormField>
                {form.watch("smokingInterventions")?.includes("OTHER") && (
                  <FormField name="smokingInterventionOther">
                    <label className={style.addReferralFormLabel}>
                      Smoking Intervention (Other)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      disabled={isReview}
                      {...form.register("smokingInterventionOther")}
                    />
                  </FormField>
                )}
              </div>

              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "20px" }}
              >
                <FormField name="alcoholRiskScore">
                  <label className={style.addReferralFormLabel}>
                    Alcohol Risk Score
                  </label>
                  <input type="hidden" {...form.register("alcoholRiskScore")} />
                  <CalculateInputTrigger
                    value={form.watch("alcoholRiskScore")}
                    onClick={() => setIsAuditCModalOpen(true)}
                    disabled={isReview}
                  />
                </FormField>
                {alcoholInterventionCategory === "DEPENDENCE" && (
                  <FormField name="alcoholDependenceInterventions">
                    <label className={style.addReferralFormLabel}>
                      Alcohol Dependence Interventions
                    </label>
                    <MultiSelect
                      options={ALCOHOL_DEPENDENCE_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("alcoholDependenceInterventions")}
                      onChange={(value) =>
                        form.setValue("alcoholDependenceInterventions", value, {
                          shouldValidate: true,
                        })
                      }
                    />
                    <InterventionInfoHints
                      options={ALCOHOL_DEPENDENCE_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch(
                        "alcoholDependenceInterventions"
                      )}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {alcoholInterventionCategory === "HIGHER_RISK" && (
                  <FormField name="alcoholHigherRiskInterventions">
                    <label className={style.addReferralFormLabel}>
                      Alcohol Higher Risk Interventions
                    </label>
                    <MultiSelect
                      options={ALCOHOL_HIGHER_RISK_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("alcoholHigherRiskInterventions")}
                      onChange={(value) =>
                        form.setValue("alcoholHigherRiskInterventions", value, {
                          shouldValidate: true,
                        })
                      }
                    />
                    <InterventionInfoHints
                      options={ALCOHOL_HIGHER_RISK_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch(
                        "alcoholHigherRiskInterventions"
                      )}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {alcoholInterventionCategory === "INCREASING_RISK" && (
                  <FormField name="alcoholIncreasingRiskInterventions">
                    <label className={style.addReferralFormLabel}>
                      Alcohol Increasing Risk Interventions
                    </label>
                    <MultiSelect
                      options={ALCOHOL_INCREASING_RISK_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("alcoholIncreasingRiskInterventions")}
                      onChange={(value) =>
                        form.setValue(
                          "alcoholIncreasingRiskInterventions",
                          value,
                          { shouldValidate: true }
                        )
                      }
                    />
                    <InterventionInfoHints
                      options={ALCOHOL_INCREASING_RISK_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch(
                        "alcoholIncreasingRiskInterventions"
                      )}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
              </div>

              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "20px" }}
              >
                <FormField name="physicalActivityQ1">
                  <label className={style.addReferralFormLabel}>
                    Physical activity – active days
                  </label>
                  <Select
                    options={PHYSICAL_ACTIVITY_Q1_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("physicalActivityQ1")}
                    onChange={(value) =>
                      form.setValue("physicalActivityQ1", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                  <p className={style.qRiskHint}>
                    In a typical week, how many days have you been physically
                    active (PA) for total of 30 minutes or more?
                  </p>
                </FormField>
                <FormField name="physicalActivityQ2">
                  <label className={style.addReferralFormLabel}>
                    Physical activity – weekly target
                  </label>
                  <Select
                    options={PHYSICAL_ACTIVITY_Q2_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("physicalActivityQ2")}
                    onChange={(value) =>
                      form.setValue("physicalActivityQ2", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                  <p className={style.qRiskHint}>
                    If four days or less, in a typical week have you been PA for
                    either 150 minutes moderate or 75 minutes vigorous activity?
                  </p>
                </FormField>
                {!!isLowActivity && (
                  <FormField name="physicalActivityInterventions">
                    <label className={style.addReferralFormLabel}>
                      Physical Activity Interventions
                    </label>
                    <MultiSelect
                      options={PHYSICAL_ACTIVITY_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("physicalActivityInterventions")}
                      onChange={(value) =>
                        form.setValue("physicalActivityInterventions", value, {
                          shouldValidate: true,
                        })
                      }
                    />
                    <InterventionInfoHints
                      options={PHYSICAL_ACTIVITY_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch(
                        "physicalActivityInterventions"
                      )}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
              </div>

              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "20px" }}
              >
                {weightMeccBand === WEIGHT_MECC_BAND.HIGH && (
                  <FormField name="weightHighRiskInterventions">
                    <label className={style.addReferralFormLabel}>
                      Weight High Risk Interventions
                    </label>
                    <MultiSelect
                      options={WEIGHT_HIGH_RISK_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("weightHighRiskInterventions")}
                      onChange={(value) =>
                        form.setValue("weightHighRiskInterventions", value, {
                          shouldValidate: true,
                        })
                      }
                    />
                    <InterventionInfoHints
                      options={WEIGHT_HIGH_RISK_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("weightHighRiskInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {weightMeccBand === WEIGHT_MECC_BAND.INCREASED && (
                  <FormField name="weightIncreasedRiskInterventions">
                    <label className={style.addReferralFormLabel}>
                      Weight Increased Risk Interventions
                    </label>
                    <MultiSelect
                      options={WEIGHT_INCREASED_RISK_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("weightIncreasedRiskInterventions")}
                      onChange={(value) =>
                        form.setValue(
                          "weightIncreasedRiskInterventions",
                          value,
                          {
                            shouldValidate: true,
                          }
                        )
                      }
                    />
                    <InterventionInfoHints
                      options={WEIGHT_INCREASED_RISK_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch(
                        "weightIncreasedRiskInterventions"
                      )}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {weightMeccBand === WEIGHT_MECC_BAND.NORMAL && (
                  <FormField name="weightNormalInterventions">
                    <label className={style.addReferralFormLabel}>
                      Weight Normal Interventions
                    </label>
                    <MultiSelect
                      options={WEIGHT_NORMAL_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("weightNormalInterventions")}
                      onChange={(value) =>
                        form.setValue("weightNormalInterventions", value, {
                          shouldValidate: true,
                        })
                      }
                    />
                    <InterventionInfoHints
                      options={WEIGHT_NORMAL_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("weightNormalInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
      <AuditCCalculatorModal
        isOpen={isAuditCModalOpen}
        onClose={() => setIsAuditCModalOpen(false)}
        onConfirm={(
          score,
          q1,
          q2,
          q3,
          fullAuditScore,
          q4,
          q5,
          q6,
          q7,
          q8,
          q9,
          q10
        ) => {
          form.setValue("alcoholRiskScore", score, { shouldValidate: true });
          form.setValue("alcoholFullAuditScore", fullAuditScore ?? "");
          form.setValue("auditCQ1", q1);
          form.setValue("auditCQ2", q2);
          form.setValue("auditCQ3", q3);
          form.setValue("auditQ4", q4);
          form.setValue("auditQ5", q5);
          form.setValue("auditQ6", q6);
          form.setValue("auditQ7", q7);
          form.setValue("auditQ8", q8);
          form.setValue("auditQ9", q9);
          form.setValue("auditQ10", q10);
        }}
        prefillQ1={form.watch("auditCQ1")}
        prefillQ2={form.watch("auditCQ2")}
        prefillQ3={form.watch("auditCQ3")}
        prefillQ4={form.watch("auditQ4")}
        prefillQ5={form.watch("auditQ5")}
        prefillQ6={form.watch("auditQ6")}
        prefillQ7={form.watch("auditQ7")}
        prefillQ8={form.watch("auditQ8")}
        prefillQ9={form.watch("auditQ9")}
        prefillQ10={form.watch("auditQ10")}
      />
      {!hasEstablishedCvd && (
        <QRisk3CalculatorModal
          isOpen={isQRisk3ModalOpen}
          onClose={() => setIsQRisk3ModalOpen(false)}
          onConfirm={(
            score,
            ratio,
            ethrisk,
            conditions,
            cigarettesPerDay,
            extras
          ) => {
            form.setValue("qRisk3Score", score, { shouldValidate: true });
            form.setValue("tcHdlRatio", ratio);
            form.setValue(
              "qRisk3TotalCholesterol",
              extras?.totalCholesterol ?? ""
            );
            form.setValue("qRisk3Hdl", extras?.hdl ?? "");
            form.setValue("qRisk3Sbps5", extras?.sbps5 ?? "");
            form.setValue("qRisk3Ethrisk", ethrisk);
            form.setValue("qRisk3Conditions", conditions);
            form.setValue("qRisk3CigarettesPerDay", cigarettesPerDay);
          }}
          onFieldChange={({
            totalCholesterol,
            hdl,
            sbps5,
            ethrisk,
            cigarettesPerDay,
          }) => {
            form.setValue("qRisk3TotalCholesterol", totalCholesterol ?? "");
            form.setValue("qRisk3Hdl", hdl ?? "");
            form.setValue("qRisk3Sbps5", sbps5 ?? "");
            form.setValue("qRisk3Ethrisk", ethrisk);
            form.setValue("qRisk3CigarettesPerDay", cigarettesPerDay ?? "");
          }}
          prefillBmi={data?.bmi}
          prefillSbp={data?.bp1}
          prefillSmokingStatus={form.watch("smokingStatus")}
          prefillTotalCholesterol={form.watch("qRisk3TotalCholesterol")}
          prefillHdl={form.watch("qRisk3Hdl")}
          prefillSbps5={form.watch("qRisk3Sbps5")}
          prefillEthrisk={form.watch("qRisk3Ethrisk")}
          prefillConditions={form.watch("qRisk3Conditions")}
          prefillCigarettesPerDay={form.watch("qRisk3CigarettesPerDay")}
          // Seed the calculator from the ethnicity chosen on this review's
          // initial step (falls back to the patient record inside the modal).
          selectedEthnicityCode={data?.ethnicityCode}
        />
      )}
      {shouldShowCha2ds2vascScore && (
        <CHA2DS2VASCCalculatorModal
          isOpen={isCha2ds2vascModalOpen}
          onClose={() => setIsCha2ds2vascModalOpen(false)}
          onConfirm={(score, factors) => {
            form.setValue("cha2ds2vascScore", score, { shouldValidate: true });
            form.setValue("cha2ds2vascFactors", factors);
          }}
          prefillFactors={form.watch("cha2ds2vascFactors")}
        />
      )}
    </ItemFormWrapper>
  );
};
